import { useCommonStore } from '@/stores/CommonStore'
import i18n from '@/i18n'
import router from '@/router'
import { AxiosError } from 'axios'
import rg4js from 'raygun4js'

export interface HandleErrorOptions {
  error: unknown
  action: string
  showErrorMessage?: boolean
  customErrorMessage?: string
  showModal?: boolean
}

export interface ErrorWithMessage {
  message: string
}

export interface InternalErrorResponse {
  internalErrorCode: number
  message?: string
}

export interface RaygunCustomData {
  action?: string
}

export const useError = () => {
  const showGenericErrorModal = (message?: string): void => {
    const { setErrorModal } = useCommonStore()

    setErrorModal({
      showErrorModal: true,
      title: i18n.global.t('errorModal.header'),
      errorMessage: message ?? '',
      showGenericError: !message,
      showFixError: Boolean(message)
    })
  }

  const handleError = (options: HandleErrorOptions) => {
    const {
      error,
      action,
      showErrorMessage,
      customErrorMessage,
      showModal = true
    } = options

    const { setErrorModal } = useCommonStore()

    // checking for promise error on 401s before interceptor does its job
    if (error == 'Error' || error == undefined) return
    // double check for 401 errors from Axios
    if (
      isAxiosError(error) &&
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      // should not make it here. Axios response interceptor should handle 401 errors, so just send the user to logout
      router.push('logout')
    }
    // Checks to see if user is offline
    else if (isErrorObj(error) && error.message == 'Network Error') {
      setErrorModal({
        showErrorModal: true,
        title: i18n.global.t('errorModal.header'),
        errorMessage: showErrorMessage ? customErrorMessage : '',
        showGenericError: !showErrorMessage || false,
        showFixError: showErrorMessage || false
      })

      return
    }
    // if we send a custom error message, display the message, but don't send to Raygun
    else if (showErrorMessage && customErrorMessage) {
      setErrorModal({
        showErrorModal: true,
        title: i18n.global.t('errorModal.header'),
        errorMessage: customErrorMessage,
        showGenericError: !showErrorMessage || false,
        showFixError: showErrorMessage || false
      })
      return
    }
    // else send to raygun and display to user if showErrorMessage == true
    else {
      // send to raygun
      const _customData: RaygunCustomData = {}

      if (action) _customData.action = action
      if (isErrorObj(error)) {
        rg4js('send', { error: error, customData: [_customData] })
      } else {
        rg4js('send', {
          error: new Error(`Error not formatted correctly: ${error}`),
          customData: [_customData]
        })
      }

      if (!showModal) return

      const commonStore = useCommonStore()
      commonStore.setErrorModal({
        showErrorModal: true,
        title: i18n.global.t('errorModal.header'),
        errorMessage: '',
        showGenericError: true,
        showFixError: false
      })
    }
  }

  const isAxiosError = (error: unknown): error is AxiosError =>
    error instanceof AxiosError

  const isErrorObj = (error: unknown): error is Error =>
    error && typeof error === 'object'
      ? 'name' in error && 'message' in error
      : false

  const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'

  const hasInternalErrorCode = (
    error: unknown
  ): error is AxiosError<InternalErrorResponse> =>
    isAxiosError(error) &&
    error.response !== undefined &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'internalErrorCode' in error.response.data

  const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
    if (isErrorWithMessage(maybeError)) {
      return maybeError
    }

    try {
      return new Error(JSON.stringify(maybeError))
    } catch {
      // fallback in case there's an error stringifying the maybeError
      // like with circular references for example.
      return new Error(String(maybeError))
    }
  }

  const getErrorMessage = (error: unknown) => toErrorWithMessage(error).message

  return {
    handleError,
    showGenericErrorModal,
    isAxiosError,
    isErrorObj,
    isErrorWithMessage,
    hasInternalErrorCode,
    toErrorWithMessage,
    getErrorMessage
  }
}
